(ns ui.openrouter.sidebar-panel
  "The OpenRouter model catalog as an editor2 sidebar panel.

   Deliberately NOT `ui.editor2.sidebar-panel-ai`: that namespace is the
   AI panel our users see today and it stays untouched. This one is
   reachable only under the `:openrouter` sidebar key, which nothing in
   the UI sets — see `ui.openrouter.devtools` for how we open it."
  (:require [parts.replicant.query :as query]
            [ui.icons :as icons]
            [ui.openrouter.catalog :as catalog]))

(defn ai-icon [selected?]
  [:svg.size-6
   {:xmlns "http://www.w3.org/2000/svg"
    :fill "none"
    :viewBox "0 0 24 24"
    :stroke-width "1.5"
    :stroke "currentColor"
    :class ["size-6" (when-not selected? "text-slate-400")]}
   [:path
    {:stroke-linecap "round"
     :stroke-linejoin "round"
     :d "M4.5 12a7.5 7.5 0 0 0 15 0m-15 0a7.5 7.5 0 1 1 15 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077 1.41-.513m14.095-5.13 1.41-.513M5.106 17.785l1.15-.964m11.49-9.642 1.149-.964M7.501 19.795l.75-1.3m7.5-12.99.75-1.3m-6.063 16.658.26-1.477m2.605-14.772.26-1.477m0 17.726-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205 12 12m6.894 5.785-1.149-.964M6.256 7.178l-1.15-.964m15.352 8.864-1.41-.513M4.954 9.435l-1.41-.514M12.002 12l-3.75 6.495"}]])

(defn ai-entry
  [selected?]
  [:div
   {:class ["relative" "flex" "pt-3" "pb-3" "pl-2" "pr-2" "flex-col" "justify-center" "items-center" "gap-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-600"
            (when selected?
              "bg-gray-800")]
    :on {:click [[:store/assoc-in [:editor2 :sidebar-panel] :openrouter]
                 [:data/query catalog/models-query]]}}
   [:span
    {:class ["absolute" "top-0" "right-0" "px-1.5" "py-0.5" "text-[9px]" "font-bold" "leading-none" "text-white" "bg-red-500" "rounded-full" "uppercase" "tracking-wide"]}
    "NEW"]
   (ai-icon selected?)
   [:span
    {:class ["text-center" "text-xs" "font-medium" "font-medium" (when-not selected? "text-slate-400")]}
    "AI"]])

(defn subtitle-generator-promo
  []
  [:div.pb-5
   [:h3.text-base.font-semibold.text-white-900.flex.items-center.gap-2
    "FREE Subtitle Generator"
    [:span
     {:class ["px-1.5" "py-0.5" "text-[9px]" "font-bold" "leading-none" "text-white" "bg-red-500" "rounded-full" "uppercase" "tracking-wide"]}
     "NEW"]]
   [:div.mt-2.max-w-xl.text-sm.text-white-500
    [:p
     "Auto-generate per-word transcripts and burn animated captions
       with active-word highlights directly into your video — all with
       our free online tool."]
    [:div.grid.grid-cols-2.md:grid-cols-2.gap-4.mt-5
     [:div
      [:video.h-auto.max-w-full.rounded-lg
       {:src "/img/ai/subtitle_generator_1.mp4"
        :autoplay true
        :loop true
        :muted true
        :playsinline true
        :replicant/on-mount (fn [{:keys [replicant/node]}]
                              (set! (.-muted node) true))}]]
     [:div
      [:video.h-auto.max-w-full.rounded-lg
       {:src "/img/ai/subtitle_generator_2.mp4"
        :autoplay true
        :loop true
        :muted true
        :playsinline true
        :replicant/on-mount (fn [{:keys [replicant/node]}]
                              (set! (.-muted node) true))}]]]]
   [:div.mt-5
    [:a.w-full.flex.items-center.justify-center.bg-gray-500.rounded-md.p-2.text-white.no-underline
     {:href "https://www.storrito.com/tools/subtitle-generator/"
      :target "_blank"
      :rel "noopener"
      :on {:click [[:editor2/track-promotion-click
                    {:category "promotion"
                     :action "click"
                     :name "subtitle-generator"}]]}}
     [:svg
      {:xmlns "http://www.w3.org/2000/svg"
       :viewBox "0 0 24 24"
       :fill "currentColor"
       :width "20"
       :height "20"}
      [:path
       {:d "M19 4H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z"}]]
     [:span.hidden.lg:block.ml-2
      "Open free tool"]]]])

(defn separator
  []
  [:div.separator.bg-gray-500
   {:style {:width "100%"
            :height 0
            :border-top "1px solid #ededed"
            :margin-top "10px"
            :margin-bottom "10px"}}])

(defn badge-chip
  [badge]
  (case badge
    :top-performer [:span
                    {:class ["px-1.5" "py-0.5" "text-[9px]" "font-bold" "leading-none" "rounded-full" "bg-amber-400" "text-gray-900" "whitespace-nowrap"]}
                    "Best quality"]
    :best-value [:span
                 {:class ["px-1.5" "py-0.5" "text-[9px]" "font-bold" "leading-none" "rounded-full" "bg-emerald-400" "text-gray-900" "whitespace-nowrap"]}
                 "Best value"]
    nil))

(defn model-row
  [category-id model]
  [:button.w-full.text-left.rounded-lg.px-2.py-1.5.hover:bg-gray-600
   {:type "button"
    :on {:click [[:ui/modal-open :modals/openrouter-generate
                  {:model model
                   :category-id category-id
                   :source :editor2}]]}}
   [:div.flex.items-center.justify-between.gap-2
    [:span.text-sm.font-medium.truncate
     (:model/name model)]
    (badge-chip (:model/badge model))]
   [:div.flex.items-baseline.justify-between.gap-2
    [:span.text-xs.text-slate-400.truncate
     (:model/description model)]
    [:span.text-xs.text-slate-300.whitespace-nowrap
     (:model/price-label model)]]])

(defn search-box
  [state]
  [:div.relative.w-full.mb-2
   (icons/search ["w-4" "h-4" "absolute" "left-2.5" "top-2.5" "text-slate-400" "pointer-events-none"])
   [:input
    {:type "search"
     :placeholder "Search AI models…"
     :class ["w-full" "rounded-md" "bg-gray-900" "border" "border-gray-600" "pl-8" "pr-2" "py-1.5" "text-sm" "placeholder-slate-400" "focus:outline-none" "focus:border-gray-400"]
     :value (get-in state [:editor2 :ai-sidebar :search] "")
     :on {:input [[:store/assoc-in [:editor2 :ai-sidebar :search]
                   :event/target.value]]}}]])

(defn category-section
  [state searching? category]
  (let [category-id (:category/id category)
        models (:category/models category)
        expanded? (contains? (get-in state [:editor2 :ai-sidebar :expanded] #{})
                             category-id)]
    [:div.pb-3
     [:h3.text-sm.font-semibold.text-white-900.mb-1
      (:category/label category)]
     (map (partial model-row category-id)
          (if searching?
            models
            (catalog/visible-models models expanded?)))
     (when (and (not searching?)
                (> (count models) catalog/collapsed-count))
       [:button.text-xs.text-blue-400.hover:text-blue-300.px-2.py-1
        {:type "button"
         :on {:click [[:openrouter/toggle-category category-id]]}}
        (if expanded?
          "Show less"
          (str "Show all (" (count models) ")"))])]))

(defn model-catalog
  [state]
  (let [query-result (query/get-result state catalog/models-query)
        search (get-in state [:editor2 :ai-sidebar :search] "")
        searching? (seq search)]
    (cond
      (and (nil? query-result)
           (query/loading? state catalog/models-query))
      [:div.flex.items-center.justify-center.py-10
       [:div.w-10.h-10.border-4.border-blue-500.border-t-transparent.rounded-full.animate-spin]]

      (and (nil? query-result)
           (query/error? state catalog/models-query))
      [:div.flex.flex-col.items-center.py-6
       [:p.text-sm.text-slate-400.mb-3
        "The AI models could not be loaded."]
       [:button.flex.items-center.justify-center.bg-gray-500.rounded-md.p-2
        {:type "button"
         :on {:click [[:data/query catalog/models-query]]}}
        "Retry"]]

      (nil? query-result)
      nil

      :else
      (let [categories (catalog/filter-categories
                         (:openrouter/categories query-result)
                         search)]
        [:div.flex.flex-col.w-full
         [:h3.text-base.font-semibold.text-white-900.mb-2
          "AI Generation"]
         (search-box state)
         (if (empty? categories)
           [:p.text-sm.text-slate-400.py-4.text-center
            (str "No models match \"" search "\"")]
           (map (partial category-section state searching?)
                categories))]))))

(defn panel
  [state]
  [:div
   {:class ["flex" "flex-col" "gap-2" "w-full"]}
   (model-catalog state)
   (separator)
   (subtitle-generator-promo)])
